import {Injectable, Optional, SkipSelf} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {GjeldendeLokale, Lokaleinformasjon} from "./GjeldendeLokale";
import {OversettelserRegister} from "felles-ng2/tjenester/i18n/OversettelserRegister";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";

const debug = initDebugger('Oversettelser');

/**
 * Tjeneste som gir tilgang til tekster i de ulike språk vi har oversettelser for.
 *
 * Oversettelser lastes via tjenesten {@link OversettelserRegister}.
 */
@Injectable()
export class Oversettelser {

    constructor(gjeldendeLokale: GjeldendeLokale,
                private readonly translate: TranslateService,
                @Optional() @SkipSelf() annenInstans: Oversettelser) {
        if (annenInstans) throw 'Oversettelser skal ikke ha flere instanser. Har du kanskje registrert en i "providers" i en komponent eller importert InternasjonaliseringModul?';

        gjeldendeLokale.brukersLokaleSomStrøm()
          .subscribe(lokale => this.setAktivtLokale(lokale));
    }

    /**
     * Oversett tekst med gjeldene locale.
     *
     * NB: For at tekst-nøkkel som er parameter til denne funksjonen skal bli ekstrahert til pot-fil
     * må du huske å omslutte teksten med i18n('Tekstnøkkelen min, med parameter (merk navnet..) {etParameter}')
     * F.eks. translateService.oversett(i18n('Tekstnøkkelen min'), {etParameter: "Jeg er verdien til parameteret"});
     * @param nøkkel
     * @param params
     */
    oversett(nøkkel: string, params = {}) {
        return this.translate.instant(nøkkel, params);
    }

    //hjelpemetoder

    private setAktivtLokale(lokale: Lokaleinformasjon) {
        debug('setAktivtLokale %s', lokale.somLocaleId())
        this.translate.setDefaultLang(lokale.somLocaleId());
        this.translate.use(lokale.somLocaleId());
    }
}

/**
 * Omslutt tekster som skal oversettes i ts-verdenen, f.eks. i en komponent eller tjeneste med denne marker-funksjonen
 * Den fanges opp av ngx-translate-extract
 * @param key
 */
export function i18n<T extends string | string[]>(key: T): T {
    return key;
}

/**
 * Internasjonalisert tekst.
 *
 * Merk at konstruktør må kalles med nøkkel-argumentet pakket inn i i18n() metoden for at nøkkel skal plukkes opp av jobben som ekstraherer nøkler.
 * todo - sørge for at det holder med feks i18nTekst('hei på deg')
 *
 * Eks: `new I18nTekst(i18n('hei på deg'))`
 */
export class I18nTekst {

    constructor(private readonly nøkkel: string, private readonly argumenter?: I18nParametre) {
    }

    oversett(oversettelser: Oversettelser): string {
        return oversettelser.oversett(this.nøkkel, this.argumenter);
    }
}

export type I18nParametre = Record<string, string|number>;

/** Internasjonalisert tekst, som potensielt er null */
export type I18nTekstMuligNull = (oversettelser: Oversettelser) => string | null;

